import * as React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@leagueplatform/auth-utils';
import { buildTenantPath } from '@web-config-app/core';
import { ErrorPage } from '@web-config-app/core-react-ui';
import { isValidTenantId } from '../../utils/is-valid-tenant-id';
import { resolveTenantIdAlias } from '../../utils/resolve-tenant-id-alias';
import { useTenantId } from '../../hooks/use-tenant-id/use-tenant-id';
import { LoadingIndicator } from '../loading-indicator.component';
import {
  SESSION_STORAGE_AUTH_RETURN_TO,
  SESSION_STORAGE_LOGOUT_INITIATED,
} from '../../constants/storage-key';
import { useDiscoveryTokenClaims } from '../../hooks/use-discovery-token-claims/use-discovery-token-claims';
import { captureMessageWithOwner } from '../../utils/sentry-helpers';

/**
 * Rendered at the root `/` route. Guards against premature redirects and
 * waits for Auth0 callback processing before navigating to a tenant route.
 *
 * Tenant clearing (logout, stale IDs) is handled by `useResolvedTenant` in
 * `AppContent`; this component only decides *when* to redirect.
 *
 * - After Auth0 domain-discovery login, uses `useDiscoveryTokenClaims` to
 *   decode the discovery token and extract the selected `tenant_id`.
 *   `useDiscoveryTokenClaims` retries the token fetch to handle the race
 *   where Auth0 hasn't finished caching the token after the redirect.
 * - Once the tenant is resolved, sets it via `useTenantId` and navigates
 *   to the tenant-scoped route. `useResolvedTenant` in `AppContent` then
 *   resolves the real config, and `LeagueProvider` updates the global
 *   config store via `setConfig`.
 * - On `?error=access_denied`, shows an error page with a logout CTA.
 * - On `?code=`, waits for `LeagueProvider`'s `checkAuth` to finish before
 *   redirecting to the tenant route without the auth query params.
 * - Otherwise redirects to the stored tenant route for this tab (or a saved
 *   deep-link).
 */
export const RootHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantId, setTenantId } = useTenantId();
  const { isAuthenticated } = useAuth({
    redirectToLogInWhenSignedOut: true,
  });

  // After Auth0 domain-discovery login, fetch and decode the discovery token
  // to extract the tenant_id the user selected. Enabled only once
  // authenticated and before a tenant has been resolved. Uses
  // fetchTokenWithRetry internally to handle the post-redirect race.
  const {
    tenantId: discoveryTenantId,
    accessibleTenantIds,
    isLoading: isDiscoveryTokenLoading,
    error: discoveryError,
  } = useDiscoveryTokenClaims(
    Boolean(isAuthenticated) && (!tenantId || !isValidTenantId(tenantId)),
  );

  const resolvedDiscoveryTenantId = React.useMemo(() => {
    const normalizedDiscoveryTenantId = discoveryTenantId
      ? resolveTenantIdAlias(discoveryTenantId)
      : null;

    const normalizedAccessibleTenantIds = [
      ...new Set(
        accessibleTenantIds
          .map((accessibleTenantId) => resolveTenantIdAlias(accessibleTenantId))
          .filter((accessibleTenantId) => isValidTenantId(accessibleTenantId)),
      ),
    ];

    if (
      normalizedDiscoveryTenantId &&
      isValidTenantId(normalizedDiscoveryTenantId) &&
      normalizedAccessibleTenantIds.includes(normalizedDiscoveryTenantId)
    ) {
      return normalizedDiscoveryTenantId;
    }

    return normalizedAccessibleTenantIds[0] ?? null;
  }, [discoveryTenantId, accessibleTenantIds]);

  // Once we have the tenant_id from the discovery token, set it and navigate
  // to the tenant-scoped route. This triggers useResolvedTenant in AppContent
  // to resolve the real config, updating LeagueProvider's config store.
  React.useEffect(() => {
    if (resolvedDiscoveryTenantId && !tenantId) {
      setTenantId(resolvedDiscoveryTenantId);
      navigate(buildTenantPath(resolvedDiscoveryTenantId), { replace: true });
    }
  }, [resolvedDiscoveryTenantId, tenantId, setTenantId, navigate]);

  // Capture the deep-link destination once via ref so it survives React
  // StrictMode double-renders (reading + removing sessionStorage in the
  // render body is a side effect that breaks under double-invocation).
  const savedReturnToRef = React.useRef(
    sessionStorage.getItem(SESSION_STORAGE_AUTH_RETURN_TO),
  );

  React.useEffect(() => {
    if (savedReturnToRef.current) {
      sessionStorage.removeItem(SESSION_STORAGE_AUTH_RETURN_TO);
    }
  }, []);

  const logoutInitiated = sessionStorage.getItem(
    SESSION_STORAGE_LOGOUT_INITIATED,
  );
  const hasAuthCallback = location.search.includes('code=');
  const searchParams = new URLSearchParams(location.search);
  const hasAccessDenied = searchParams.get('error') === 'access_denied';
  const errorDescription = searchParams.get('error_description');

  // Auth0 returned access_denied — the user's org does not have access to
  // the resolved tenant. Capture for observability; the ErrorPage below
  // shows a logout CTA so the user can re-auth from a clean state.
  React.useEffect(() => {
    if (hasAccessDenied) {
      captureMessageWithOwner(
        `RootHandler: Auth0 access_denied${
          tenantId ? ` for tenant "${tenantId}"` : ''
        }`,
        {
          severityLevel: 'warning',
          context: {
            auth0: {
              errorDescription: errorDescription ?? 'none',
              tenantId: tenantId || 'none',
            },
          },
        },
      );
    }
  }, [hasAccessDenied, errorDescription, tenantId]);

  if (hasAccessDenied) {
    return (
      <ErrorPage
        heading="TENANT_ACCESS_DENIED_HEADING"
        secondaryCta={{
          label: 'LOG_OUT',
          to: '/sign-out',
        }}
      />
    );
  }

  // Show a loading indicator while waiting for auth and discovery-token
  // resolution. If retries are exhausted, show an error page with a logout
  // CTA instead of spinning forever.
  if (!tenantId || !isValidTenantId(tenantId)) {
    if (isAuthenticated !== true) {
      return <LoadingIndicator />;
    }
    if (discoveryError) {
      return (
        <ErrorPage
          heading="DISCOVERY_TOKEN_ERROR_HEADING"
          secondaryCta={{
            label: 'LOG_OUT',
            to: '/sign-out',
          }}
        />
      );
    }
    if (!isDiscoveryTokenLoading && !resolvedDiscoveryTenantId) {
      return (
        <ErrorPage
          heading="DISCOVERY_TOKEN_ERROR_HEADING"
          secondaryCta={{
            label: 'LOG_OUT',
            to: '/sign-out',
          }}
        />
      );
    }
    return <LoadingIndicator />;
  }

  if (logoutInitiated) {
    return null;
  }

  // Auth0 callback in progress — wait for LeagueProvider's checkAuth to
  // resolve before redirecting.  Redirecting now would forward stale
  // ?code= params and race with checkAuth.
  if (hasAuthCallback && isAuthenticated === undefined) {
    return <LoadingIndicator />;
  }

  if (savedReturnToRef.current) {
    return <Navigate to={savedReturnToRef.current} replace />;
  }

  return <Navigate to={buildTenantPath(tenantId)} replace />;
};
